import React from 'react'
import styled from 'styled-components'
import { cohortsService } from '../services/apiService'
import { useAuth } from '../context/AuthContext'
import ContentViewer from '../components/ContentViewer'
import ResponsiveLayout from '../components/ResponsiveLayout'

const CohortsPanelContainer = styled.div`
  width: 100%;
`

const PanelTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 1.5rem;

  @media (min-width: 640px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }
`

const Button = styled.button`
  background-color: #2563eb;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  min-height: 44px;
  width: 100%;
  
  &:hover {
    background-color: #1d4ed8;
  }

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  @media (min-width: 640px) {
    width: auto;
  }
`

const CohortsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1.5rem;
  }
`

const CohortCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 1.25rem;
  border: 1px solid #e5e5e5;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  @media (min-width: 640px) {
    padding: 1.5rem;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
`

const CohortName = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #111827;
  line-height: 1.4;

  @media (min-width: 640px) {
    font-size: 1.25rem;
  }
`

const CohortDescription = styled.p`
  color: #6b7280;
  margin-bottom: 1rem;
  line-height: 1.5;
  font-size: 0.95rem;
`

const CohortStats = styled.div`
  display: flex;
  justify-content: space-between;
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const CohortActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: auto;
`

const ActionButton = styled.button`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  min-height: 44px;
  flex: 1;
  min-width: 0;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  &.primary {
    background-color: #2563eb;
    color: white;
    border-color: #2563eb;

    &:hover {
      background-color: #1d4ed8;
      border-color: #1d4ed8;
    }
  }

  &.danger {
    color: #dc2626;
    border-color: #fecaca;
    background-color: #fef2f2;

    &:hover {
      background-color: #fee2e2;
      border-color: #fca5a5;
    }
  }

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`

const ModalContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);

  @media (min-width: 640px) {
    padding: 2rem;
  }
`

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  transition: border-color 0.2s;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  background: white;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`

const FileInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`

const ContentSection = styled.div`
  margin-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
  padding-top: 1.5rem;
`

const SectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "📁";
    font-size: 1.1rem;
  }
`

const ContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ContentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #f9fafb;
  transition: all 0.2s;
  gap: 1rem;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`

const ContentInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const ContentTitle = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ContentMeta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`

const ContentActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;

  @media (max-width: 640px) {
    justify-content: stretch;
  }
`

const SmallActionButton = styled.button`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  min-height: 44px;
  min-width: 44px;
  flex: 1;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  &.view {
    background-color: #3b82f6;
    color: white;
    border-color: #3b82f6;

    &:hover {
      background-color: #2563eb;
      border-color: #2563eb;
    }
  }

  &.delete {
    color: #dc2626;
    border-color: #fecaca;
    background-color: #fef2f2;

    &:hover {
      background-color: #fee2e2;
      border-color: #fca5a5;
    }
  }

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;

  @media (min-width: 640px) {
    padding: 3rem 2rem;
  }
`

const EmptyStateTitle = styled.p`
  font-size: 1.125rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`

const EmptyStateSubtitle = styled.p`
  color: #9ca3af;
  margin-bottom: 1.5rem;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`

// NEW: Textbook Search Components
const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  max-height: 300px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`

const SearchResultItem = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`

const TextbookTitle = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
`

const TextbookAuthors = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
`

const TextbookDetails = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`

const TextbookMetadata = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
`

const MetadataRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const MetadataLabel = styled.span`
  font-weight: 600;
  color: #374151;
`

const MetadataValue = styled.span`
  color: #6b7280;
  text-align: right;
`

function CohortsPanel() {
  const [cohorts, setCohorts] = React.useState([])
  const [cohortContent, setCohortContent] = React.useState({})
  const [loading, setLoading] = React.useState(true)
  const [showCreateModal, setShowCreateModal] = React.useState(false)
  const [showContentModal, setShowContentModal] = React.useState(false)
  const [showContentViewer, setShowContentViewer] = React.useState(false)
  const [selectedCohort, setSelectedCohort] = React.useState(null)
  const [selectedContent, setSelectedContent] = React.useState(null)
  const [newCohort, setNewCohort] = React.useState({ name: '', description: '' })
  const [newContent, setNewContent] = React.useState({
    title: '',
    content_type: 'pdf',
    file: null
  })
  
  // NEW: Textbook search state
  const [textbookSearchQuery, setTextbookSearchQuery] = React.useState('')
  const [textbookSearchResults, setTextbookSearchResults] = React.useState([])
  const [isSearching, setIsSearching] = React.useState(false)
  const [selectedTextbook, setSelectedTextbook] = React.useState(null)
  const { user } = useAuth()

  // NEW: Real-time textbook search
  React.useEffect(() => {
    const searchTextbooks = async () => {
      if (textbookSearchQuery.length < 2) {
        setTextbookSearchResults([])
        return
      }

      setIsSearching(true)
      try {
        const response = await cohortsService.searchTextbooks(textbookSearchQuery)
        setTextbookSearchResults(response.textbooks || [])
      } catch (error) {
        console.error('Textbook search error:', error)
        setTextbookSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }

    const timeoutId = setTimeout(searchTextbooks, 300)
    return () => clearTimeout(timeoutId)
  }, [textbookSearchQuery])

  // Fetch cohorts and their content
  React.useEffect(() => {
    const fetchCohorts = async () => {
      setLoading(true)
      try {
        console.log('👥 Fetching cohorts from backend...')
        const response = await cohortsService.getCohorts()
        setCohorts(response.cohorts || [])
        console.log(`✅ Loaded ${response.cohorts?.length || 0} cohorts`)
        
        // Fetch content for each cohort
        const contentMap = {}
        for (const cohort of response.cohorts || []) {
          try {
            const contentResponse = await cohortsService.getCohortContent(cohort.id)
            contentMap[cohort.id] = contentResponse.content || []
          } catch (error) {
            console.error(`❌ Failed to fetch content for cohort ${cohort.id}:`, error)
            contentMap[cohort.id] = []
          }
        }
        setCohortContent(contentMap)
        
      } catch (error) {
        console.error('❌ Failed to fetch cohorts:', error)
        setCohorts([])
      } finally {
        setLoading(false)
      }
    }

    fetchCohorts()
  }, [])

  const createCohort = async () => {
    if (!newCohort.name.trim()) {
      alert('Please enter a cohort name')
      return
    }

    try {
      console.log('Creating new cohort:', newCohort)
      const createdCohort = await cohortsService.createCohort(newCohort)
      setCohorts(prev => [...prev, createdCohort])
      setCohortContent(prev => ({ ...prev, [createdCohort.id]: [] }))
      setShowCreateModal(false)
      setNewCohort({ name: '', description: '' })
      alert('Cohort created successfully!')
    } catch (error) {
      console.error('❌ Failed to create cohort:', error)
      alert('Failed to create cohort. Please try again.')
    }
  }

  const deleteCohort = async (cohortId) => {
    if (!confirm('Are you sure you want to delete this cohort? All content will be permanently deleted.')) {
      return
    }

    try {
      await cohortsService.deleteCohort(cohortId)
      setCohorts(prev => prev.filter(cohort => cohort.id !== cohortId))
      setCohortContent(prev => {
        const newContent = { ...prev }
        delete newContent[cohortId]
        return newContent
      })
      alert('Cohort deleted successfully!')
    } catch (error) {
      console.error('❌ Failed to delete cohort:', error)
      alert('Failed to delete cohort. Please try again.')
    }
  }

  const suggestImprovements = async (cohortId) => {
    try {
      console.log(`💡 Generating improvement suggestions for cohort ${cohortId}`)
      const suggestions = await cohortsService.suggestImprovements(cohortId)
      alert(`Improvement Suggestions:\n\n${suggestions.suggestions}`)
    } catch (error) {
      console.error('❌ Failed to generate suggestions:', error)
      alert('Failed to generate improvement suggestions. Please try again.')
    }
  }

  const openContentModal = (cohort) => {
    setSelectedCohort(cohort)
    setShowContentModal(true)
    setNewContent({
      title: '',
      content_type: 'pdf',
      file: null
    })
    // NEW: Reset textbook search when opening modal
    setTextbookSearchQuery('')
    setTextbookSearchResults([])
    setSelectedTextbook(null)
  }

  // NEW: Handle textbook selection
  const handleTextbookSelect = (textbook) => {
    setSelectedTextbook(textbook)
    setNewContent(prev => ({
      ...prev,
      title: textbook.title,
      content_type: 'book'
    }))
    setTextbookSearchQuery('')
    setTextbookSearchResults([])
  }

  // NEW: Clear textbook selection
  const clearTextbookSelection = () => {
    setSelectedTextbook(null)
    setNewContent(prev => ({
      ...prev,
      title: '',
      content_type: 'pdf'
    }))
  }

  const addContentToCohort = async () => {
    if (!newContent.title.trim()) {
      alert('Please enter a title for the content')
      return
    }

    if (!newContent.file && newContent.content_type !== 'web' && newContent.content_type !== 'book') {
      alert('Please select a file to upload')
      return
    }

    if (newContent.content_type === 'web' && !newContent.url?.trim()) {
      alert('Please enter a URL for the web page')
      return
    }

    // NEW: Validate textbook selection
    if (newContent.content_type === 'book' && !selectedTextbook) {
      alert('Please select a textbook from the search results')
      return
    }

    try {
      const formData = new FormData()
      formData.append('title', newContent.title)
      formData.append('content_type', newContent.content_type)
      
      if (newContent.file) {
        formData.append('file', newContent.file)
      }
      
      // Include URL for web content
      const contentData = {}
      if (newContent.content_type === 'web' && newContent.url) {
        contentData.url = newContent.url
      }
      
      // NEW: Include textbook metadata for book content
      if (newContent.content_type === 'book' && selectedTextbook) {
        contentData.textbook_metadata = selectedTextbook
      }
      
      formData.append('content_data', JSON.stringify(contentData))

      console.log(`Adding content to cohort ${selectedCohort.id}`)
      const newContentItem = await cohortsService.addContentToCohort(selectedCohort.id, formData)
      
      // Update local state
      setCohortContent(prev => ({
        ...prev,
        [selectedCohort.id]: [...(prev[selectedCohort.id] || []), newContentItem]
      }))
      
      // Refresh cohorts count
      const response = await cohortsService.getCohorts()
      setCohorts(response.cohorts || [])
      
      setShowContentModal(false)
      setSelectedCohort(null)
      setSelectedTextbook(null) // NEW: Clear textbook selection
      alert('Content added successfully!')
    } catch (error) {
      console.error('❌ Failed to add content:', error)
      const message = error.response?.data?.detail || 'Failed to add content. Please try again.'
      alert(message)
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setNewContent(prev => ({
        ...prev,
        file: file,
        title: prev.title || file.name // Auto-fill title if empty
      }))
    }
  }

  const viewContent = (cohort, contentItem) => {
    setSelectedContent({ cohort, contentItem })
    setShowContentViewer(true)
  }

  const deleteContentItem = async (cohortId, contentId) => {
    if (!confirm('Are you sure you want to delete this content item? This action cannot be undone.')) {
      return
    }

    try {
      await cohortsService.deleteContentItem(cohortId, contentId)
      
      // Update local state
      setCohortContent(prev => ({
        ...prev,
        [cohortId]: (prev[cohortId] || []).filter(item => item.id !== contentId)
      }))
      
      // Refresh cohorts count
      const response = await cohortsService.getCohorts()
      setCohorts(response.cohorts || [])
      
      alert('Content item deleted successfully!')
    } catch (error) {
      console.error('❌ Failed to delete content item:', error)
      alert('Failed to delete content item. Please try again.')
    }
  }

  if (loading) {
    return (
      <ResponsiveLayout>
        <CohortsPanelContainer>
          <PanelTitle>Cohort Management</PanelTitle>
          <LoadingState>Loading your cohorts...</LoadingState>
        </CohortsPanelContainer>
      </ResponsiveLayout>
    )
  }

  return (
    <ResponsiveLayout>
      <CohortsPanelContainer>
        <PanelHeader>
          <PanelTitle>Cohort Management</PanelTitle>
          <Button onClick={() => setShowCreateModal(true)}>
            Create New Cohort
          </Button>
        </PanelHeader>

        <CohortsGrid>
          {cohorts.map(cohort => (
            <CohortCard key={cohort.id}>
              <CohortName>{cohort.name}</CohortName>
              <CohortDescription>
                {cohort.description || 'No description provided.'}
              </CohortDescription>
              <CohortStats>
                <span>{cohort.content_count || 0} content items</span>
                <span>Created {new Date(cohort.created_at).toLocaleDateString()}</span>
              </CohortStats>
              
              {/* Content List */}
              {cohortContent[cohort.id] && cohortContent[cohort.id].length > 0 && (
                <ContentSection>
                  <SectionTitle>Content Items</SectionTitle>
                  <ContentList>
                    {cohortContent[cohort.id].map(content => (
                      <ContentItem key={content.id}>
                        <ContentInfo>
                          <ContentTitle title={content.title}>
                            {content.title}
                          </ContentTitle>
                          <ContentMeta>
                            <span>{content.content_type}</span>
                            <span>{content.created_at ? new Date(content.created_at).toLocaleDateString() : 'No date'}</span>
                          </ContentMeta>
                        </ContentInfo>
                        <ContentActions>
                          <SmallActionButton 
                            className="view"
                            onClick={() => viewContent(cohort, content)}
                          >
                            View
                          </SmallActionButton>
                          <SmallActionButton 
                            className="delete"
                            onClick={() => deleteContentItem(cohort.id, content.id)}
                          >
                            Delete
                          </SmallActionButton>
                        </ContentActions>
                      </ContentItem>
                    ))}
                  </ContentList>
                </ContentSection>
              )}
              
              <CohortActions>
                <ActionButton 
                  className="primary" 
                  onClick={() => openContentModal(cohort)}
                >
                  Add Content
                </ActionButton>
                <ActionButton onClick={() => suggestImprovements(cohort.id)}>
                  Suggest Improvements
                </ActionButton>
                <ActionButton className="danger" onClick={() => deleteCohort(cohort.id)}>
                  Delete Cohort
                </ActionButton>
              </CohortActions>
            </CohortCard>
          ))}
        </CohortsGrid>

        {cohorts.length === 0 && (
          <EmptyState>
            <EmptyStateTitle>No cohorts created yet</EmptyStateTitle>
            <EmptyStateSubtitle>
              Create your first cohort to organize your course materials!
            </EmptyStateSubtitle>
            <Button 
              onClick={() => setShowCreateModal(true)}
            >
              Create Your First Cohort
            </Button>
          </EmptyState>
        )}

        {/* Create Cohort Modal */}
        {showCreateModal && (
          <Modal onClick={(e) => e.target === e.currentTarget && setShowCreateModal(false)}>
            <ModalContent>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>
                Create New Cohort
              </h2>
              <Input
                type="text"
                placeholder="Cohort Name (e.g., CS101 - Fall 2024)"
                value={newCohort.name}
                onChange={(e) => setNewCohort({...newCohort, name: e.target.value})}
              />
              <TextArea
                placeholder="Cohort Description (optional)"
                value={newCohort.description}
                onChange={(e) => setNewCohort({...newCohort, description: e.target.value})}
              />
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <ActionButton onClick={() => setShowCreateModal(false)}>
                  Cancel
                </ActionButton>
                <ActionButton className="primary" onClick={createCohort}>
                  Create Cohort
                </ActionButton>
              </div>
            </ModalContent>
          </Modal>
        )}

        {/* Add Content Modal */}
        {showContentModal && selectedCohort && (
          <Modal onClick={(e) => e.target === e.currentTarget && setShowContentModal(false)}>
            <ModalContent>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>
                Add Content to {selectedCohort.name}
              </h2>
              
              <Select
                value={newContent.content_type}
                onChange={(e) => {
                  setNewContent({...newContent, content_type: e.target.value})
                  // NEW: Clear textbook selection when changing content type
                  if (e.target.value !== 'book') {
                    setSelectedTextbook(null)
                  }
                }}
              >
                <option value="pdf">PDF Document</option>
                <option value="pptx">PowerPoint Presentation</option>
                <option value="docx">Word Document</option>
                <option value="xlsx">Excel Spreadsheet</option>
                <option value="web">Web Page URL</option>
                <option value="book">Textbook</option> {/* NEW: Textbook option */}
                <option value="moodle">Moodle Content</option>
                <option value="sharepoint">SharePoint Site</option>
              </Select>

              {/* NEW: Textbook Search Interface */}
              {newContent.content_type === 'book' && (
                <>
                  <SearchContainer>
                    <Input
                      type="text"
                      placeholder="Search for textbooks..."
                      value={textbookSearchQuery}
                      onChange={(e) => setTextbookSearchQuery(e.target.value)}
                      disabled={!!selectedTextbook}
                    />
                    {textbookSearchResults.length > 0 && !selectedTextbook && (
                      <SearchResults>
                        {textbookSearchResults.map((textbook, index) => (
                          <SearchResultItem 
                            key={index} 
                            onClick={() => handleTextbookSelect(textbook)}
                          >
                            <TextbookTitle>{textbook.title}</TextbookTitle>
                            <TextbookAuthors>by {textbook.authors?.join(', ') || 'Unknown Author'}</TextbookAuthors>
                            <TextbookDetails>
                              <span>{textbook.edition}</span>
                              <span>{textbook.publication_year}</span>
                              <span>{textbook.publisher}</span>
                            </TextbookDetails>
                          </SearchResultItem>
                        ))}
                      </SearchResults>
                    )}
                  </SearchContainer>

                  {/* Selected Textbook Display */}
                  {selectedTextbook && (
                    <TextbookMetadata>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
                          Selected Textbook
                        </h3>
                        <button 
                          onClick={clearTextbookSelection}
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: '#dc2626', 
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          Change
                        </button>
                      </div>
                      <MetadataRow>
                        <MetadataLabel>Title:</MetadataLabel>
                        <MetadataValue>{selectedTextbook.title}</MetadataValue>
                      </MetadataRow>
                      <MetadataRow>
                        <MetadataLabel>Authors:</MetadataLabel>
                        <MetadataValue>{selectedTextbook.authors?.join(', ') || 'Unknown'}</MetadataValue>
                      </MetadataRow>
                      <MetadataRow>
                        <MetadataLabel>Edition:</MetadataLabel>
                        <MetadataValue>{selectedTextbook.edition}</MetadataValue>
                      </MetadataRow>
                      <MetadataRow>
                        <MetadataLabel>Publisher:</MetadataLabel>
                        <MetadataValue>{selectedTextbook.publisher || 'Unknown'}</MetadataValue>
                      </MetadataRow>
                      <MetadataRow>
                        <MetadataLabel>Year:</MetadataLabel>
                        <MetadataValue>{selectedTextbook.publication_year || 'Unknown'}</MetadataValue>
                      </MetadataRow>
                      <MetadataRow>
                        <MetadataLabel>Discipline:</MetadataLabel>
                        <MetadataValue>{selectedTextbook.discipline || 'General'}</MetadataValue>
                      </MetadataRow>
                    </TextbookMetadata>
                  )}
                </>
              )}

              {/* Title Input (hidden when textbook is selected) */}
              {newContent.content_type !== 'book' && (
                <Input
                  type="text"
                  placeholder="Content Title"
                  value={newContent.title}
                  onChange={(e) => setNewContent({...newContent, title: e.target.value})}
                />
              )}

              {/* URL Input for web content */}
              {newContent.content_type === 'web' && (
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={newContent.url || ''}
                  onChange={(e) => setNewContent({...newContent, url: e.target.value})}
                />
              )}

              {/* File Input (shown for all types except web, optional for book) */}
              {newContent.content_type !== 'web' && (
                <FileInput
                  type="file"
                  onChange={handleFileChange}
                  accept={
                    newContent.content_type === 'pdf' ? '.pdf' :
                    newContent.content_type === 'pptx' ? '.pptx,.ppt' :
                    newContent.content_type === 'docx' ? '.docx,.doc' :
                    newContent.content_type === 'xlsx' ? '.xlsx,.xls' :
                    newContent.content_type === 'book' ? '.pdf' : // Textbook accepts PDF
                    '*/*'
                  }
                />
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: '1rem' }}>
                <ActionButton onClick={() => setShowContentModal(false)}>
                  Cancel
                </ActionButton>
                <ActionButton className="primary" onClick={addContentToCohort}>
                  Add Content
                </ActionButton>
              </div>
            </ModalContent>
          </Modal>
        )}

        {/* Content Viewer Modal */}
        {showContentViewer && selectedContent && (
          <ContentViewer
            cohortId={selectedContent.cohort.id}
            contentId={selectedContent.contentItem.id}
            onClose={() => {
              setShowContentViewer(false)
              setSelectedContent(null)
            }}
          />
        )}
      </CohortsPanelContainer>
    </ResponsiveLayout>
  )
}

export default CohortsPanel
